/* Step 11c-3 · find 28 (Mars Live Run Log, 1201-08) — the floor-to-sheet test counts, typed on the inventory count worksheet.
   Each row: item, location, listing qty, counted qty, unit cost (difference and value difference computed), with its own Save
   and Delete. "Add floor-to-sheet row" opens a new row here that is saved only when the user saves it with something in it
   (before, it stored an empty row whose cells could not be filled in or removed). Server: POST / PATCH / DELETE
   …/inventory-count/ref/{ref}/floor-to-sheet[/{row_id}] (backend/inventory_floor_rows.py). */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { fmtNumber } from "@/components/Amount";
import { askConfirm } from "@/lib/appDialog";
import { asArray } from "@/lib/asArray";

const FIELDS = ["item", "location", "listing_qty", "counted_qty", "unit_cost"];
const NUM = new Set(["listing_qty", "counted_qty", "unit_cost"]);
const errText = (e, lang) => (lang === "ar" && e?.response?.data?.detail_ar) || toErrMsg(e);
const toVal = (r, k) => (r?.[k] === null || r?.[k] === undefined ? "" : String(r[k]));
const num = (v) => (v === "" || v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));

function FloorRow({ saved, onSave, onDelete, disabled, t, isNew, testid }) {
  const [v, setV] = useState(() => Object.fromEntries(FIELDS.map((k) => [k, toVal(saved, k)])));
  const [busy, setBusy] = useState(false);
  const changed = FIELDS.some((k) => v[k] !== toVal(saved, k));
  const blank = FIELDS.every((k) => !String(v[k] || "").trim());
  const listing = num(v.listing_qty), counted = num(v.counted_qty), cost = num(v.unit_cost);
  const diff = listing === null || counted === null ? null : counted - listing;
  const val = diff === null || cost === null ? null : diff * cost;
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(FIELDS.map((k) => [k, NUM.has(k) ? (v[k] === "" ? null : v[k]) : v[k]]));
      await onSave(body);
    } finally { setBusy(false); }
  };
  const cell = (k, cls = "") => (
    <td className={`p-1 ${cls}`}>
      <input className={`h-7 w-full rounded border px-1 text-xs ${NUM.has(k) ? "text-end" : ""}`} dir={NUM.has(k) ? "ltr" : "auto"}
             inputMode={NUM.has(k) ? "decimal" : undefined} value={v[k]} disabled={disabled || busy}
             onChange={(e) => setV((o) => ({ ...o, [k]: e.target.value }))} data-testid={`${testid}-${k}`} />
    </td>
  );
  return (
    <tr data-testid={testid} data-changed={changed ? "1" : "0"} className={isNew ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}>
      {cell("item")}{cell("location")}{cell("listing_qty", "w-24")}{cell("counted_qty", "w-24")}
      <td className="p-1 text-end" dir="ltr" data-testid={`${testid}-diff`}>{diff === null ? "—" : fmtNumber(diff)}</td>
      {cell("unit_cost", "w-24")}
      <td className="p-1 text-end" dir="ltr" data-testid={`${testid}-value`}>{val === null ? "—" : fmtNumber(val)}</td>
      <td className="whitespace-nowrap p-1 text-end">
        <button type="button" className="rounded border border-emerald-600 px-1.5 py-0.5 text-[11px] text-emerald-700 disabled:opacity-40"
                disabled={disabled || busy || !changed || blank} onClick={save} data-testid={`${testid}-save`}>
          {t("s11c3Inv.save")}</button>
        <button type="button" className="ms-1 rounded border border-red-300 px-1.5 py-0.5 text-[11px] text-red-700 disabled:opacity-40"
                disabled={disabled || busy} onClick={onDelete} data-testid={`${testid}-delete`}>
          {isNew ? t("s11c3Inv.discard") : t("s11c3Inv.delete")}</button>
      </td>
    </tr>
  );
}

export default function InventoryFloorRows({ engId, wsRef, rows, adding, setAdding, onChanged, disabled, t, lang }) {
  const base = `/engagements/${engId}/inventory-count/ref/${wsRef}/floor-to-sheet`;
  const floorCols = useResizableColumns({ storageKey: "inv-floor_to_sheet", defaults: [150, 140, 110, 110, 100, 110, 130, 150], mins: [100, 96, 90, 90, 80, 90, 100, 110] });
  const saveNew = async (body) => {
    try { await api.post(base, body); toast.success(t("s11c3Inv.saved")); setAdding(false); await onChanged(); }
    catch (e) { toast.error(errText(e, lang)); }
  };
  const saveRow = (id) => async (body) => {
    try { await api.patch(`${base}/${id}`, body); toast.success(t("s11c3Inv.saved")); await onChanged(); }
    catch (e) { toast.error(errText(e, lang)); }
  };
  const delRow = (id) => async () => {
    if (!(await askConfirm(t("s11c3Inv.confirmDelete"), { danger: true }))) return;
    try { await api.delete(`${base}/${id}`); toast.success(t("s11c3Inv.deleted")); await onChanged(); }
    catch (e) { toast.error(errText(e, lang)); }
  };
  const list = asArray(rows);
  return (
    <div className="mt-2" data-testid="inv-floor_to_sheet-table">
      <div className="text-xs font-medium">{t("inv6.floorToSheet")}</div>
      <div className="overflow-auto rounded border">
        <ResizableTable cols={floorCols} className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <ResizableTh index={0} className="p-1 text-start">{t("inv6.testColItem")}</ResizableTh>
              <ResizableTh index={1} className="p-1 text-start">{t("inv6.testColLocation")}</ResizableTh>
              <ResizableTh index={2} className="p-1 text-end">{t("inv6.listingQty")}</ResizableTh>
              <ResizableTh index={3} className="p-1 text-end">{t("inv6.countedQty")}</ResizableTh>
              <ResizableTh index={4} className="p-1 text-end">{t("inv6.diff")}</ResizableTh>
              <ResizableTh index={5} className="p-1 text-end">{t("inv6.unitCost")}</ResizableTh>
              <ResizableTh index={6} className="p-1 text-end">{t("inv6.valueDiff")}</ResizableTh>
              <ResizableTh index={7} className="p-1"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && !adding && (
              <tr><td colSpan={8} className="p-2 text-center text-muted-foreground" data-testid="inv-f2s-empty">{t("s11c3Inv.none")}</td></tr>
            )}
            {list.map((r) => (
              <FloorRow key={`${r.id}:${r.updated_at || r.created_at || ""}`} saved={r} t={t} disabled={disabled}
                        onSave={saveRow(r.id)} onDelete={delRow(r.id)} testid={`inv-f2s-row-${r.id}`} />
            ))}
            {adding && (
              <FloorRow key="new" saved={{}} isNew t={t} disabled={disabled} onSave={saveNew}
                        onDelete={() => setAdding(false)} testid="inv-f2s-new" />
            )}
          </tbody>
        </ResizableTable>
      </div>
      {adding && <div className="mt-1 text-[11px] text-muted-foreground" data-testid="inv-f2s-new-hint">{t("s11c3Inv.newHint")}</div>}
    </div>
  );
}
